import React, { useEffect, useRef } from "react";
import { usePresence } from "../../../hooks/usePresence";
import { POPOVER_EXIT_MS } from "../../../design-system/tokens/popoverMotion";
import { createPortal } from "react-dom";
import {
  Wrap,
  Menu,
  MenuItemButton,
  ItemIcon,
  ItemContent,
  ItemLabel,
  ItemDescription,
  ItemHint,
  ItemShortcut,
  Divider,
  SectionLabel,
  Footer,
  SoonSlot,
  ItemIconTile,
  ItemTrailing,
} from "./ActionMenu.styles";
import { SoonBadge, SOON_TOOLTIP } from "../../atoms/SoonBadge";
import { Tooltip } from "../../atoms/Tooltip";
import type { ActionMenuProps } from "./ActionMenu.types";

const DEFAULT_MIN_MENU_WIDTH = 200;

/**
 * Config-driven action menu (role="menu", fire-and-close) — the action-flavoured
 * sibling of molecules/Dropdown (which keeps select/listbox semantics).
 * Items come from components/registry; open state + usePortalMenu live in the parent.
 */
export function ActionMenu<A extends string = string>({
  items,
  onAction,
  trigger,
  isOpen,
  onClose,
  menuRef,
  menuRect,
  ariaLabel,
  ariaDescribedBy,
  minMenuWidth = DEFAULT_MIN_MENU_WIDTH,
  align = "right",
  iconColor,
  autoFocus = false,
  highlightFirst = false,
  showScrollbar = false,
  density = "default",
  focusStyle = "ring",
  soonAlign = "inline",
  soonLabel,
  soonBadge = "hover",
  currentTint,
  header,
  variant = "default",
  footer,
}: ActionMenuProps<A>) {
  // Keep the panel mounted at its last position while the exit animation plays.
  const { mounted } = usePresence(isOpen, POPOVER_EXIT_MS);
  const lastRectRef = useRef(menuRect);
  if (menuRect) lastRectRef.current = menuRect;

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Arrows, Home and End move between the enabled items (Enter runs the focused one).
      const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
      if (!keys.includes(e.key) || !menuRef.current) return;
      // Typing in a header field keeps its own cursor keys.
      if (e.target instanceof Element && e.target.closest("input, textarea")) return;
      const items = Array.from(
        menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'),
      );
      if (items.length === 0) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement as HTMLButtonElement);
      const target =
        e.key === "Home" ? 0
        : e.key === "End" ? items.length - 1
        : e.key === "ArrowDown" ? (at + 1) % items.length
        : (at <= 0 ? items.length : at) - 1;
      items[target].focus();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, menuRef]);

  const isPlaced = menuRect !== null;
  useEffect(() => {
    if (!isOpen || !isPlaced || !autoFocus) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [isOpen, isPlaced, autoFocus, menuRef]);

  const rect = menuRect ?? (isOpen ? null : lastRectRef.current);

  const width = rect ? Math.max(rect.width, minMenuWidth) : 0;
  const left =
    rect === null
      ? 0
      : align === "right"
        ? rect.left + rect.width - width
        : rect.left;

  return (
    <Wrap>
      {trigger}
      {mounted &&
        rect &&
        createPortal(
          <Menu
            ref={menuRef}
            role="menu"
            $closing={!isOpen}
            $panel={variant === "panel"}
            aria-label={ariaLabel}
            $showScrollbar={showScrollbar}
            $density={density}
            aria-describedby={ariaDescribedBy}
            style={{
              ...(rect.bottom != null
                ? { bottom: rect.bottom, top: "auto" }
                : { top: rect.top, bottom: "auto" }),
              left,
              width,
              maxHeight: rect.maxHeight,
            }}
          >
            {header && <li role="none">{header}</li>}
            {items.map(
              (
                {
                id,
                label,
                description,
                hint,
                icon: Icon,
                danger,
                disabled,
                soon,
                shortcut,
                dividerBefore,
                leading,
                trailing,
                current,
                sectionLabel,
                endHint,
              },
                index,
              ) => {
                const button = (
                    <MenuItemButton
                      type="button"
                      role="menuitem"
                      $danger={danger}
                      $highlight={highlightFirst && index === 0}
                      $roomy={density === "roomy"}
                      $fillFocus={focusStyle === "fill"}
                      $density={density}
                      $currentTint={current ? currentTint : undefined}
                      disabled={disabled}
                      aria-disabled={soon || undefined}
                      aria-label={soon ? `${label}, coming soon` : undefined}
                      $panel={variant === "panel"}
                      aria-current={current ? "true" : undefined}
                      onClick={() => {
                        if (soon) return;
                        onAction(id);
                        onClose();
                      }}
                    >
                      {leading}
                      {!leading && Icon && density === "tiled" && (
                        <ItemIconTile $current={current}>
                          <Icon size={14} />
                        </ItemIconTile>
                      )}
                      {!leading && Icon && density !== "tiled" && (
                        <ItemIcon $danger={danger} $color={iconColor}>
                          <Icon size={14} />
                        </ItemIcon>
                      )}
                      {description ? (
                        <ItemContent>
                          <ItemLabel>{label}</ItemLabel>
                          <ItemDescription>{description}</ItemDescription>
                        </ItemContent>
                      ) : (
                        label
                      )}
                      {endHint && <ItemHint>{endHint}</ItemHint>}
                      {trailing && <ItemTrailing>{trailing}</ItemTrailing>}
                      {shortcut && <ItemShortcut>{shortcut}</ItemShortcut>}
                      {soon && (
                        <SoonSlot $always={soonBadge === "always" || soonAlign === "end"}>
                          <SoonBadge label={soonLabel} />
                        </SoonSlot>
                      )}
                    </MenuItemButton>
                );
                return (
                  <React.Fragment key={id}>
                    {sectionLabel && <SectionLabel role="presentation">{sectionLabel}</SectionLabel>}
                    {dividerBefore && <Divider role="separator" />}
                    <li role="none">
                      {soon ? (
                        <Tooltip label={SOON_TOOLTIP} position="left">
                          {button}
                        </Tooltip>
                      ) : hint ? (
                        <Tooltip label={hint} position="left">
                          {button}
                        </Tooltip>
                      ) : (
                        button
                      )}
                    </li>
                  </React.Fragment>
                );
              },
            )}
            {footer && <Footer role="none">{footer}</Footer>}
          </Menu>,
          document.body,
        )}
    </Wrap>
  );
}

export default ActionMenu;
